/**
 * Iter209af — Pagina Email.
 *
 * Client email full-screen a 3 colonne (sidebar cartelle / lista / lettura).
 *
 * Iter209ak — Integrazione Zoho Mail:
 *   - operatore con casella collegata: la lista carica le email reali dalla
 *     casella dell'organizzazione via `GET /email/inbox` (IMAP zoho.eu).
 *   - admin: nuovo tab "Operatori" che elenca le caselle dell'organizzazione
 *     Zoho e permette di crearne di nuove associandole a un operatore
 *     HouseTag (POST /admin/email-accounts).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import {
  PencilSimple, Envelope, Star, PaperPlaneTilt, FileText, Archive,
  Trash, WarningOctagon, Tag, Plus, Gear, X, Paperclip, ArrowClockwise,
  ArrowBendUpLeft, ArrowBendDoubleUpRight, ArrowBendUpRight, Printer,
  MagnifyingGlass, TextB, TextItalic, LinkSimple, ListBullets, ArrowUp,
  Users as UsersIcon, CircleNotch, ArrowsOut, ArrowsIn,
} from "@phosphor-icons/react";

const FOLDERS = [
  { id: "inbox", label: "Posta in arrivo", icon: Envelope, unread: 0 },
  { id: "starred", label: "Speciali", icon: Star, unread: 0 },
  { id: "sent", label: "Inviati", icon: PaperPlaneTilt, unread: 0 },
  { id: "drafts", label: "Bozze", icon: FileText, unread: 0 },
  { id: "archive", label: "Archivio", icon: Archive, unread: 0 },
  { id: "spam", label: "Spam", icon: WarningOctagon, unread: 0 },
  { id: "trash", label: "Cestino", icon: Trash, unread: 0 },
];

function EmptyList({ label = "Posta in arrivo" }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm p-8" data-testid="email-list-empty">
      <Envelope size={40} weight="light" className="mb-3 text-slate-300" />
      <p className="font-medium text-slate-500">Nessuna email in {label.toLowerCase()}</p>
      <p className="text-xs mt-1">Le nuove email compariranno qui.</p>
    </div>
  );
}

function ReadingEmpty() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm p-8" data-testid="email-reader-empty">
      <Envelope size={56} weight="light" className="mb-3 text-slate-200" />
      <p className="font-medium text-slate-500">Seleziona un'email per leggerla</p>
    </div>
  );
}

function ComposeWindow({ open, onClose, signatureHtml = "", initial = null }) {
  const [to, setTo] = useState(initial?.to || "");
  const [cc, setCc] = useState(initial?.cc || "");
  const [ccn, setCcn] = useState("");
  const [subject, setSubject] = useState(initial?.subject || "");
  const [body, setBody] = useState(initial?.body || "");
  // Iter209cf — Modalità "schermo intero" per il modal Nuovo messaggio.
  // Serve ad avere spazio sufficiente per la toolbar completa senza
  // sovrapposizioni col widget Feedback Beta (z-[80], bottom-right).
  const [maximized, setMaximized] = useState(false);
  // Allegati pendenti (array di File).
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  // Iter209cr — stato di invio e salvataggio bozza.
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  // Iter209an — Al primo open, se il body è vuoto, precarica la firma
  // dell'utente in fondo (renderizzata come HTML nel rich editor).
  useEffect(() => {
    if (!open) return;
    if (body) return;
    if (signatureHtml) {
      setBody(`<p><br/></p><p><br/></p>${signatureHtml}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, signatureHtml]);
  if (!open) return null;
  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setAttachments(prev => [...prev, ...files]);
    e.target.value = ""; // consente di riaggiungere lo stesso file
  };
  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };
  // Iter209cr — Converte un File in `{filename, content_type, content_b64}`.
  const fileToPayload = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const comma = dataUrl.indexOf(",");
      const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : "";
      resolve({
        filename: f.name,
        content_type: f.type || "application/octet-stream",
        content_b64: b64,
      });
    };
    reader.onerror = () => reject(reader.error || new Error("read error"));
    reader.readAsDataURL(f);
  });
  const buildPayload = async () => {
    const atts = await Promise.all(attachments.map(fileToPayload));
    // Il RichTextEditor restituisce HTML; derivo un plain text basico
    // facendo stripping dei tag (serve come fallback per client text-only).
    const plain = (body || "").replace(/<br\s*\/?\s*>/gi, "\n")
      .replace(/<\/p\s*>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
      .trim();
    return {
      to, cc, bcc: ccn, subject,
      body_html: body || "",
      body_text: plain,
      attachments: atts,
      in_reply_to: initial?.in_reply_to || "",
      references: initial?.references || "",
    };
  };
  const validateRecipients = () => {
    const re = /[^@\s]+@[^@\s]+\.[^@\s]+/;
    const emails = (to || "").split(/[,;]/).map(s => s.trim()).filter(Boolean);
    if (!emails.length) return "Inserisci almeno un destinatario";
    const bad = emails.find(e => !re.test(e));
    if (bad) return `Email destinatario non valida: ${bad}`;
    return null;
  };
  const handleSend = async () => {
    const err = validateRecipients();
    if (err) { toast.error(err); return; }
    setSending(true);
    try {
      const payload = await buildPayload();
      await api.post("/email/send", payload);
      toast.success("Email inviata");
      onClose();
      setTo(""); setCc(""); setCcn(""); setSubject(""); setBody(""); setAttachments([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Errore invio email");
    } finally { setSending(false); }
  };
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const payload = await buildPayload();
      await api.post("/email/drafts", payload);
      toast.success("Bozza salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Errore salvataggio bozza");
    } finally { setSavingDraft(false); }
  };
  // Iter209cf — Classi dinamiche: z-[90] (sopra il FeedbackButton z-[80]).
  // Compatta: pannello bottom-right ma con margine destro di 7rem così
  // non copre il widget Feedback Beta. Espansa: overlay fullscreen.
  const outerClass = maximized
    ? "fixed inset-0 z-[90] bg-black/40 flex items-stretch justify-center p-4"
    : "fixed bottom-4 right-28 w-[560px] max-w-[96vw] z-[90]";
  const panelClass = maximized
    ? "flex flex-col w-full max-w-5xl mx-auto bg-white rounded-xl shadow-2xl border border-slate-200"
    : "flex flex-col w-full bg-white rounded-t-xl shadow-2xl border border-slate-200";
  return (
    <div
      className={outerClass}
      data-testid="email-compose"
      data-maximized={maximized ? "true" : "false"}
    >
      <div className={panelClass}>
        <div className={`flex items-center justify-between px-3 py-2 bg-slate-900 text-white ${maximized ? "rounded-t-xl" : "rounded-t-xl"}`}>
          <span className="text-sm font-medium">{initial?.title || "Nuovo messaggio"}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMaximized(m => !m)}
              className="hover:opacity-70 p-1"
              data-testid="email-compose-toggle-maximize"
              aria-label={maximized ? "Riduci" : "Schermo intero"}
              title={maximized ? "Riduci" : "Schermo intero"}
            >
              {maximized
                ? <ArrowsIn size={16} weight="bold" />
                : <ArrowsOut size={16} weight="bold" />}
            </button>
            <button onClick={onClose} className="hover:opacity-70 p-1" data-testid="email-compose-close" aria-label="Chiudi">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
        <div className={`p-3 space-y-2 flex-1 ${maximized ? "overflow-y-auto" : ""}`}>
          <div className="flex items-center gap-2 border-b py-1"><span className="text-xs text-slate-500 w-12">A:</span>
            <input className="flex-1 text-sm bg-transparent outline-none" value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinatario@esempio.it" data-testid="email-compose-to" />
          </div>
          <div className="flex items-center gap-2 border-b py-1"><span className="text-xs text-slate-500 w-12">Cc:</span>
            <input className="flex-1 text-sm bg-transparent outline-none" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="email-compose-cc" />
          </div>
          <div className="flex items-center gap-2 border-b py-1"><span className="text-xs text-slate-500 w-12">Ccn:</span>
            <input className="flex-1 text-sm bg-transparent outline-none" value={ccn} onChange={(e) => setCcn(e.target.value)} data-testid="email-compose-bcc" />
          </div>
          <div className="flex items-center gap-2 border-b py-1"><span className="text-xs text-slate-500 w-12">Oggetto:</span>
            <input className="flex-1 text-sm bg-transparent outline-none" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-compose-subject" />
          </div>
          {/* Iter209an — Rich text body con firma preinjected come HTML */}
          <div data-testid="email-compose-body-wrapper">
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Scrivi il tuo messaggio…"
              minHeight={maximized ? 400 : 180}
              testId="email-compose-body"
            />
          </div>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="email-compose-attachments">
              {attachments.map((f, i) => (
                <div key={i} className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs" data-testid={`email-compose-attachment-${i}`}>
                  <Paperclip size={12} />
                  <span className="truncate max-w-[180px]">{f.name}</span>
                  <button onClick={() => removeAttachment(i)} className="hover:text-red-600" aria-label="Rimuovi allegato">
                    <X size={12} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onPickFiles}
          className="hidden"
          data-testid="email-compose-file-input"
        />
        <div className="flex items-center justify-between border-t px-3 py-2 bg-slate-50 rounded-b-xl gap-2">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="email-compose-attach"
              title="Allega file"
            >
              <Paperclip size={14} weight="bold" />
              <span className="ml-1 hidden sm:inline">Allega</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSaveDraft}
              disabled={savingDraft || sending}
              data-testid="email-compose-draft"
            >
              {savingDraft ? "Salvataggio…" : "Salva in bozze"}
            </Button>
          </div>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={handleSend}
            disabled={sending || savingDraft}
            data-testid="email-compose-send"
          >
            <PaperPlaneTilt size={12} weight="fill" />
            <span className="ml-1">{sending ? "Invio…" : "Invia"}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Iter209ak — Pannello admin per gestire le caselle email dell'organizzazione Zoho. */
function OperatorEmailAccountsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    firstName: "", lastName: "", username: "", password: "", operatorId: "",
  });
  const [submitting, setSubmitting] = useState(false);
  // Iter209at — Errore inline dedicato al form (es. 409 duplicato).
  // Rimane visibile finché l'admin non modifica lo username.
  const [createError, setCreateError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [accRes, usrRes] = await Promise.all([
        api.get("/admin/email-accounts"),
        api.get("/users"),
      ]);
      setAccounts(accRes.data?.accounts || []);
      setUsers(Array.isArray(usrRes.data) ? usrRes.data : (usrRes.data?.users || []));
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento caselle Zoho");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.username || !form.password) {
      toast.error("Username e password obbligatori");
      return;
    }
    setSubmitting(true);
    setCreateError("");
    try {
      const payload = { ...form, operatorId: form.operatorId || undefined };
      await api.post("/admin/email-accounts", payload);
      toast.success("Casella creata correttamente.");
      setForm({ firstName: "", lastName: "", username: "", password: "", operatorId: "" });
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Creazione fallita";
      if (status === 409) {
        // Iter209at — Duplicato: NON chiudere il pannello, mostra
        // l'errore inline così l'admin può correggere subito lo
        // username senza riaprire il form.
        setCreateError(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (accountId, email) => {
    if (!window.confirm(`Eliminare la casella ${email}? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/admin/email-accounts/${accountId}`);
      toast.success("Casella eliminata.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione fallita");
    }
  };

  const findLinkedOperator = (emailAddr) => {
    const u = users.find(
      (x) => (x.zoho_email || "").toLowerCase() === (emailAddr || "").toLowerCase(),
    );
    if (!u) return null;
    const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
    return name;
  };

  return (
    <div className="space-y-6" data-testid="email-operators-admin-panel">
      <div>
        <h2 className="font-display text-lg font-semibold text-slate-900">Gestione Email Operatori</h2>
        <p className="text-xs text-slate-500">
          Crea le caselle @housetag.it sull'organizzazione Zoho e associale ai tuoi operatori.
        </p>
      </div>

      {/* Form creazione */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3" data-testid="email-account-create-form">
        <h3 className="font-display text-base font-semibold text-slate-900">Nuova casella</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Nome</label>
            <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} data-testid="email-account-first-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Cognome</label>
            <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} data-testid="email-account-last-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Username (@housetag.it)</label>
            <div className="flex items-center gap-1">
              <Input
                value={form.username}
                onChange={(e) => {
                  setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "") });
                  // Iter209at — Pulisce l'errore di duplicato appena l'admin modifica lo username
                  if (createError) setCreateError("");
                }}
                placeholder="mario.rossi"
                data-testid="email-account-username"
                aria-invalid={!!createError}
                className={createError ? "border-red-400 focus-visible:ring-red-200" : undefined}
              />
              <span className="text-xs text-slate-500 whitespace-nowrap">@housetag.it</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Password iniziale</label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="email-account-password" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-700">Associa a operatore HouseTag (opzionale)</label>
            <Select value={form.operatorId || "none"} onValueChange={(v) => setForm({ ...form, operatorId: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="email-account-operator-select">
                <SelectValue placeholder="— Nessuno —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuno —</SelectItem>
                {users
                  // Iter209ao — Solo utenti operatore (o admin) possono
                  // avere una casella @housetag.it. I `professional` (pro
                  // master) NON hanno caselle nell'org Zoho di Housetag —
                  // usano il proprio dominio via piano MyDomain.
                  .filter((u) => u.role === "admin" || (u.role || "").endsWith("_operator"))
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`email-account-operator-opt-${u.id}`}>
                      {(u.first_name || "") + " " + (u.last_name || "")}
                      {" — " + u.email + " (" + (u.role || "") + ")"}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Iter209at — Banner errore inline per duplicato (409). Il
            pannello resta aperto e l'admin corregge lo username. */}
        {createError && (
          <div
            role="alert"
            data-testid="email-account-create-error"
            className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
          >
            {createError}
          </div>
        )}
        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="email-account-create-submit"
          >
            {submitting ? (<><CircleNotch className="animate-spin" size={14} /> Creazione…</>) : "Crea casella"}
          </Button>
        </div>
      </div>

      {/* Lista caselle */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="email-accounts-list">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50">
          <h3 className="font-display text-base font-semibold text-slate-900">Caselle esistenti</h3>
          <Button size="sm" variant="ghost" onClick={load} data-testid="email-accounts-refresh">
            <ArrowClockwise size={14} /> Aggiorna
          </Button>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><CircleNotch className="animate-spin" /> Caricamento…</div>
        ) : error ? (
          <div className="p-4 text-sm text-red-600" data-testid="email-accounts-error">{error}</div>
        ) : accounts.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">Nessuna casella presente. Crea la prima con il form sopra.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2">Nome</th>
                <th className="text-left px-4 py-2">Operatore</th>
                <th className="text-left px-4 py-2">Storage</th>
                <th className="text-right px-4 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const email = (a.emailAddress && a.emailAddress[0]?.mailId) || a.primaryEmailAddress || a.incomingUserName || "—";
                const zuid = a.zuid || a.accountId || a.zoid;
                const linked = findLinkedOperator(email);
                return (
                  <tr key={zuid || email} className="border-b last:border-0 hover:bg-slate-50" data-testid={`email-account-row-${zuid}`}>
                    <td className="px-4 py-2 font-mono text-xs">{email}</td>
                    <td className="px-4 py-2">{`${a.firstName || ""} ${a.lastName || ""}`.trim() || "—"}</td>
                    <td className="px-4 py-2 text-xs">
                      {linked ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><UsersIcon size={12} weight="fill" /> {linked}</span>
                      ) : (
                        <span className="text-slate-400">non collegato</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {a.usedStorage ?? 0} / {a.planStorage ?? "?"} GB
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800" onClick={() => remove(zuid, email)} data-testid={`email-account-delete-${zuid}`}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Iter209ak — Riga singola nella lista email operatore.
 *  Iter209db — Checkbox selezione multipla (visibile on hover o quando
 *  checked). Il click sul checkbox NON apre il messaggio. */
function InboxRow({ msg, active, onClick, selected, onToggleSelect }) {
  return (
    <div
      className={`group relative border-b border-slate-100 hover:bg-slate-50 transition-colors ${
        active ? "bg-purple-50" : ""
      }`}
      data-testid={`email-row-${msg.uid}`}
    >
      <label
        className={`absolute left-2 top-2.5 z-10 flex items-center justify-center w-5 h-5 cursor-pointer ${
          selected ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
        } transition-opacity`}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={!!selected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect?.(msg.uid, e.target.checked); }}
          className="w-4 h-4 accent-purple-600 cursor-pointer"
          data-testid={`email-row-checkbox-${msg.uid}`}
          aria-label="Seleziona email"
        />
      </label>
      <button
        onClick={onClick}
        className={`w-full text-left pl-9 pr-3 py-2 flex flex-col gap-0.5 ${
          msg.unread ? "font-semibold" : ""
        }`}
      >
        <div className="flex items-center justify-between">
          <span className={`text-sm truncate max-w-[70%] ${msg.unread ? "text-slate-900" : "text-slate-700"}`}>
            {msg.from_name || msg.from_email || "—"}
          </span>
          <span className="text-[10px] text-slate-400 shrink-0">{formatDate(msg.date)}</span>
        </div>
        <div className={`text-sm truncate ${msg.unread ? "text-slate-900" : "text-slate-500"}`}>
          {msg.subject}
        </div>
      </button>
    </div>
  );
}

function formatDate(raw) {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

export default function EmailPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [selectedFolder, setSelectedFolder] = useState("inbox");
  const [composeOpen, setComposeOpen] = useState(false);
  // Iter209dzv — Prefill per Rispondi / Rispondi a tutti / Inoltra.
  const [composeInitial, setComposeInitial] = useState(null);
  const [composeKey, setComposeKey] = useState(0);
  const [labels, setLabels] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [rightTab, setRightTab] = useState("mail"); // "mail" | "operators" (solo admin)

  // Iter209ak — stato inbox reale
  const [inbox, setInbox] = useState({
    loading: true, linked: false, connected: false, needs_password: false,
    messages: [], email: "",
  });
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [downloadingAtt, setDownloadingAtt] = useState(null);
  // Iter209eq — Download allegato letto da IMAP al volo.
  const downloadAttachment = async (uid, index, filename) => {
    setDownloadingAtt(index);
    try {
      const r = await api.get(`/email/message/${encodeURIComponent(uid)}/attachment/${index}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "allegato";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Download allegato non riuscito");
    } finally {
      setDownloadingAtt(null);
    }
  };
  // Iter209cr — corpo decodificato del messaggio selezionato + delete state.
  const [messageBody, setMessageBody] = useState({ loading: false, text: "", html: "", attachments: [], error: "" });
  const [deleting, setDeleting] = useState(false);
  // Iter209db — Selezione multipla + stato azioni di gruppo.
  const [selectedUids, setSelectedUids] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelectUid = useCallback((uid, checked) => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (checked) next.add(uid); else next.delete(uid);
      return next;
    });
  }, []);

  // Iter209co — Reload inbox senza full-page refresh (triggerato
  // dall'useEffect iniziale e dal tasto Aggiorna nella barra di ricerca).
  const reloadInbox = React.useCallback(async () => {
    setInbox((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get("/email/inbox");
      setInbox({
        loading: false,
        linked: !!res.data?.linked,
        connected: !!res.data?.connected,
        needs_password: !!res.data?.needs_password,
        messages: res.data?.messages || [],
        email: res.data?.email || "",
      });
      // Iter209dc — Il backend ora ritorna anche `unread_count` nella
      // stessa connessione IMAP; propaghiamolo al badge sidebar via
      // CustomEvent per evitare una round-trip aggiuntiva al polling.
      if (typeof res.data?.unread_count === "number") {
        try {
          window.dispatchEvent(new CustomEvent("email:count", { detail: { count: res.data.unread_count } }));
        } catch { /* no-op */ }
      }
    } catch {
      setInbox({
        loading: false, linked: false, connected: false,
        needs_password: false, messages: [], email: "",
      });
    }
  }, []);

  useEffect(() => {
    // Solo operatori (o admin che non è in tab operators/domain) devono caricare l'inbox.
    if (isAdmin && rightTab !== "mail") return;
    reloadInbox();
  }, [isAdmin, rightTab, reloadInbox]);

  // Iter209cr — Carica corpo decodificato quando si seleziona un messaggio.
  // Iter209ct — Appena l'utente apre un messaggio ancora non letto,
  // sfalso l'`unread` nella lista locale (toglie il grassetto istante
  // dopo il click) e in parallelo mando `POST /email/mark-read` per
  // impostare il flag `\Seen` lato Zoho. Il backend lo fa anche sul
  // GET della singola email, questa è una sincronizzazione aggiuntiva.
  useEffect(() => {
    if (!selectedMsg?.uid) {
      setMessageBody({ loading: false, text: "", html: "", attachments: [], error: "" });
      return;
    }
    let active = true;
    setMessageBody({ loading: true, text: "", html: "", attachments: [], error: "" });
    // Marca come letta localmente SUBITO per feedback immediato.
    if (selectedMsg.unread) {
      setInbox((s) => ({
        ...s,
        messages: s.messages.map((m) =>
          m.uid === selectedMsg.uid ? { ...m, unread: false } : m
        ),
      }));
      api.post("/email/mark-read", { uids: [selectedMsg.uid] }).catch(() => {
        // Silenzioso: la marca è best-effort; UI già aggiornata.
      }).finally(() => {
        // Iter209da — notifica il badge sidebar di rifetchare l'UNSEEN.
        try { window.dispatchEvent(new Event("email:refresh")); } catch { /* no-op */ }
      });
    }
    api.get(`/email/message/${encodeURIComponent(selectedMsg.uid)}`)
      .then((r) => {
        if (!active) return;
        setMessageBody({
          loading: false,
          text: r.data?.text || "",
          html: r.data?.html || "",
          attachments: Array.isArray(r.data?.attachments) ? r.data.attachments : [],
          error: "",
          to: r.data?.to || "", cc: r.data?.cc || "", from: r.data?.from || "",
          reply_to: r.data?.reply_to || "",
          message_id: r.data?.message_id || "", references: r.data?.references || "",
        });
      })
      .catch((e) => {
        if (!active) return;
        setMessageBody({
          loading: false, text: "", html: "", attachments: [],
          error: e?.response?.data?.detail || "Impossibile caricare il contenuto",
        });
      });
    return () => { active = false; };
  }, [selectedMsg?.uid, selectedMsg?.unread]);

  // Iter209cr — Elimina (sposta in cestino) il messaggio selezionato o gli
  // UIDs passati esplicitamente. Rimuove la riga dalla lista locale.
  const openReplyCompose = (mode) => {
    if (!selectedMsg) return;
    setComposeInitial(buildReplyDraft(mode, selectedMsg, messageBody, inbox.email, user?.email_signature_html || ""));
    setComposeKey((k) => k + 1);
    setComposeOpen(true);
  };

  const deleteMessages = async (uids) => {
    const targets = (uids && uids.length ? uids : (selectedMsg?.uid ? [selectedMsg.uid] : []));
    if (!targets.length) return;
    if (!window.confirm(`Eliminare ${targets.length} email? Verranno spostate nel cestino.`)) return;
    setDeleting(true);
    try {
      const r = await api.post("/email/delete", { uids: targets });
      toast.success(`Eliminate ${r.data?.deleted ?? targets.length} email`);
      setInbox((s) => ({
        ...s,
        messages: s.messages.filter((m) => !targets.includes(m.uid)),
      }));
      if (selectedMsg?.uid && targets.includes(selectedMsg.uid)) setSelectedMsg(null);
      setSelectedUids((prev) => {
        const next = new Set(prev);
        targets.forEach((uid) => next.delete(uid));
        return next;
      });
      // Iter209da — rinfresca il badge sidebar dopo delete.
      try { window.dispatchEvent(new Event("email:refresh")); } catch { /* no-op */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally { setDeleting(false); }
  };

  // Iter209db — Azioni bulk sulla selezione corrente.
  const bulkUids = useMemo(() => Array.from(selectedUids), [selectedUids]);
  const clearSelection = () => setSelectedUids(new Set());
  const runBulkAction = async (kind) => {
    if (!bulkUids.length) return;
    if (kind === "delete") {
      await deleteMessages(bulkUids);
      return;
    }
    setBulkBusy(true);
    try {
      if (kind === "archive") {
        const r = await api.post("/email/archive", { uids: bulkUids });
        const n = r.data?.archived ?? bulkUids.length;
        toast.success(`Spostate in archivio ${n} email`);
        setInbox((s) => ({
          ...s,
          messages: s.messages.filter((m) => !bulkUids.includes(m.uid)),
        }));
        if (selectedMsg?.uid && bulkUids.includes(selectedMsg.uid)) setSelectedMsg(null);
        clearSelection();
      } else if (kind === "mark-read") {
        await api.post("/email/mark-read", { uids: bulkUids });
        setInbox((s) => ({
          ...s,
          messages: s.messages.map((m) => bulkUids.includes(m.uid) ? { ...m, unread: false } : m),
        }));
        clearSelection();
        toast.success("Segnate come lette");
      } else if (kind === "mark-unread") {
        await api.post("/email/mark-unread", { uids: bulkUids });
        setInbox((s) => ({
          ...s,
          messages: s.messages.map((m) => bulkUids.includes(m.uid) ? { ...m, unread: true } : m),
        }));
        clearSelection();
        toast.success("Segnate come non lette");
      }
      try { window.dispatchEvent(new Event("email:refresh")); } catch { /* no-op */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore operazione");
    } finally { setBulkBusy(false); }
  };

  // Iter209cg — Banner e stati operatore guidati da `linked` + `needs_password`,
  // NON da `connected` (altrimenti chi ha la casella associata ma senza
  // password IMAP settata vedrebbe "Nessun account collegato", che è falso).
  const emailAccountLinked = inbox.linked;
  const needsPassword = inbox.needs_password;
  const showAdminBanner = isAdmin && !emailAccountLinked && rightTab === "mail";
  const showAdminPasswordPrompt =
    isAdmin && emailAccountLinked && needsPassword && rightTab === "mail";
  // Un operatore senza casella associata NON deve vedere il client email;
  // mostra lo stato dedicato con istruzioni.
  const showOperatorNotConfigured = !isAdmin && !inbox.loading && !emailAccountLinked;
  const showOperatorNeedsPassword =
    !isAdmin && !inbox.loading && emailAccountLinked && needsPassword;

  const activeFolder = useMemo(
    () => FOLDERS.find((f) => f.id === selectedFolder) || FOLDERS[0],
    [selectedFolder],
  );

  const addLabel = () => {
    const l = newLabel.trim();
    if (!l) return;
    setLabels((prev) => (prev.includes(l) ? prev : [...prev, l]));
    setNewLabel("");
  };

  // Iter209df — Ogni cartella selezionata fetcha dal backend. L'INBOX
  // viene gia' caricata al mount via `reloadInbox`; cambiando folder
  // (Inviati/Bozze/Cestino/Archivio/Spam) chiamiamo `/email/folder/{id}`
  // e salviamo i messaggi in `folderCache` per non rifare la round-trip
  // ad ogni click. Un refresh esplicito o un cambio di casella bustano
  // la cache.
  const [folderCache, setFolderCache] = useState({}); // {id: {messages, loading, loaded}}
  useEffect(() => {
    if (selectedFolder === "inbox") return;
    if (!inbox.connected) return;
    if (folderCache[selectedFolder]?.loaded || folderCache[selectedFolder]?.loading) return;
    let active = true;
    setFolderCache((c) => ({ ...c, [selectedFolder]: { messages: [], loading: true, loaded: false } }));
    // Iter209dq — Encode difensivo del folder_id: oggi sono tutti ASCII fissi
    // (`inbox`, `sent`, ...), ma così la URL resta sicura anche se dovessimo
    // supportare in futuro cartelle Zoho custom con caratteri speciali.
    api.get(`/email/folder/${encodeURIComponent(selectedFolder)}`)
      .then((r) => {
        if (!active) return;
        setFolderCache((c) => ({
          ...c,
          [selectedFolder]: {
            messages: r.data?.messages || [],
            loading: false,
            loaded: true,
          },
        }));
      })
      .catch(() => {
        if (!active) return;
        setFolderCache((c) => ({
          ...c,
          [selectedFolder]: { messages: [], loading: false, loaded: true, error: true },
        }));
      });
    return () => { active = false; };
  }, [selectedFolder, inbox.connected, folderCache]);

  const messagesToShow = selectedFolder === "inbox"
    ? inbox.messages
    : (folderCache[selectedFolder]?.messages || []);

  return (
    <div className="flex flex-col h-full min-h-[80vh]" data-testid="email-page">
      {showAdminBanner && (
        <div
          className="mb-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-sm"
          data-testid="email-admin-no-account-banner"
        >
          Nessun account email collegato al tuo profilo admin. Puoi comunque gestire le caselle degli operatori dalla scheda <button className="underline font-medium" onClick={() => setRightTab("operators")}>Operatori</button>.
        </div>
      )}

      {showAdminPasswordPrompt && (
        <div
          className="mb-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-center justify-between gap-3"
          data-testid="email-admin-needs-password-banner"
        >
          <span>
            La casella <strong>{inbox.email}</strong> è associata al tuo profilo ma manca la password IMAP.
            Imposta la password della casella per attivare invio e ricezione.
          </span>
          <Link
            to="/users"
            className="underline font-medium whitespace-nowrap"
            data-testid="email-admin-needs-password-link"
          >
            Vai alla scheda operatore
          </Link>
        </div>
      )}

      {/* Iter209bk — Rimossa la tab admin "Gestione Email Operatori":
          la creazione/associazione della casella Zoho avviene
          automaticamente alla accettazione del link di invito. Le
          modifiche a password/email di una casella esistente sono ora
          nella pagina Utenti (scheda operatore). */}

      {showOperatorNotConfigured ? (
        <div
          className="flex-1 flex items-center justify-center min-h-[60vh]"
          data-testid="email-operator-not-configured"
        >
          <div className="max-w-md bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Envelope size={28} weight="fill" className="text-amber-600" />
            </div>
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">
              Casella email non ancora configurata
            </h2>
            <p className="text-sm text-slate-600">
              La tua casella email non è ancora stata configurata. Contatta il tuo amministratore.
            </p>
          </div>
        </div>
      ) : showOperatorNeedsPassword ? (
        <div
          className="flex-1 flex items-center justify-center min-h-[60vh]"
          data-testid="email-operator-needs-password"
        >
          <div className="max-w-md bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Envelope size={28} weight="fill" className="text-amber-600" />
            </div>
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">
              Casella associata ma non ancora attiva
            </h2>
            <p className="text-sm text-slate-600">
              La tua casella <strong>{inbox.email}</strong> è collegata ma manca la password IMAP. Chiedi al tuo amministratore di impostarla (Reset password nella tua scheda operatore) per attivare invio e ricezione.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex gap-3 min-h-[70vh]">
          {/* Sidebar */}
          <aside className="w-56 shrink-0 flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm p-3" data-testid="email-sidebar">
            <Button
              onClick={() => setComposeOpen(true)}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white mb-3 shadow"
              data-testid="email-compose-open"
            >
              <PencilSimple size={14} weight="fill" /> Scrivi
            </Button>
            <nav className="space-y-1 mb-4">
              {FOLDERS.map((f) => {
                const Ic = f.icon;
                const active = selectedFolder === f.id;
                const unread = f.id === "inbox" ? inbox.messages.filter((m) => m.unread).length : f.unread;
                return (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFolder(f.id)}
                    className={`w-full flex items-center justify-between text-sm px-2 py-1.5 rounded-md transition-colors ${
                      active ? "bg-purple-50 text-purple-800" : "text-slate-700 hover:bg-slate-100"
                    }`}
                    data-testid={`email-folder-${f.id}`}
                  >
                    <span className="flex items-center gap-2"><Ic size={14} weight={active ? "fill" : "regular"} /> {f.label}</span>
                    {unread > 0 && (
                      <span className="text-[10px] font-bold bg-purple-600 text-white rounded-full px-1.5 py-0.5" data-testid={`email-folder-${f.id}-unread`}>
                        {unread}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="border-t pt-3 flex-1">
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mb-1 flex items-center justify-between">
                Etichette
                <button
                  onClick={() => {
                    const el = document.getElementById("email-label-input");
                    if (el) el.focus();
                  }}
                  className="p-1 rounded hover:bg-slate-100"
                  aria-label="Aggiungi etichetta"
                  data-testid="email-label-plus"
                >
                  <Plus size={12} />
                </button>
              </div>
              <div className="flex items-center gap-1 mb-2">
                <Input
                  id="email-label-input"
                  className="h-7 text-xs"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Nuova etichetta"
                  onKeyDown={(e) => { if (e.key === "Enter") addLabel(); }}
                  data-testid="email-label-input"
                />
                <Button size="sm" variant="ghost" onClick={addLabel} data-testid="email-label-add">
                  <Plus size={12} />
                </Button>
              </div>
              <ul className="space-y-1">
                {labels.map((l) => (
                  <li key={l} className="text-xs px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-2" data-testid={`email-label-${l}`}>
                    <Tag size={12} className="text-purple-500" /> {l}
                  </li>
                ))}
                {labels.length === 0 && (
                  <li className="text-[11px] italic text-slate-400 px-2">Nessuna etichetta</li>
                )}
              </ul>
            </div>
            {inbox.email && (
              <div className="mt-2 border-t pt-2 text-[11px] text-slate-500 font-mono" data-testid="email-account-badge">
                {inbox.email}
              </div>
            )}
            <Link
              to="/email/settings"
              className="mt-3 border-t pt-3 text-sm text-slate-600 hover:text-slate-900 flex items-center gap-2"
              data-testid="email-settings-link"
            >
              <Gear size={14} /> Impostazioni email
            </Link>
          </aside>

          {/* Lista email */}
          <section className="w-96 shrink-0 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col" data-testid="email-list">
            <div className="flex items-center gap-2 px-3 py-2 border-b">
              <div className="flex-1 relative">
                <MagnifyingGlass size={14} className="absolute top-1/2 -translate-y-1/2 left-2 text-slate-400" />
                <Input className="h-8 pl-7 text-sm" placeholder="Cerca in email" data-testid="email-search" />
              </div>
              {/* Iter209co — Aggiorna inbox IMAP senza full-page reload. */}
              <button
                type="button"
                onClick={reloadInbox}
                disabled={inbox.loading}
                title="Aggiorna posta in arrivo"
                aria-label="Aggiorna"
                className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                data-testid="email-refresh-btn"
              >
                <ArrowClockwise
                  size={14}
                  weight="bold"
                  className={inbox.loading ? "animate-spin" : ""}
                />
              </button>
            </div>
            {/* Iter209db / Iter209dg — Action bar bulk evidenziata.
                 Sfondo gradient viola, icone grandi (size 18) + label
                 testuali, sticky in alto per restare visibile anche
                 scrollando la lista. */}
            {selectedUids.size > 0 ? (
              <div
                className="sticky top-0 z-20 flex items-center flex-wrap gap-2 border-y-2 border-purple-400 bg-gradient-to-r from-purple-600 to-indigo-600 px-3 py-2 text-white shadow-md"
                data-testid="email-bulk-action-bar"
              >
                <div className="flex items-center gap-2 pr-2">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/20 text-sm font-bold"
                    data-testid="email-bulk-count"
                  >
                    {selectedUids.size}
                  </span>
                  <span className="text-sm font-semibold">
                    {selectedUids.size === 1 ? "email selezionata" : "email selezionate"}
                  </span>
                </div>
                <div className="h-6 w-px bg-white/30" />
                <button
                  onClick={() => runBulkAction("delete")}
                  disabled={bulkBusy}
                  className="flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 active:bg-white/30 px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="email-bulk-delete"
                  title="Sposta nel cestino"
                >
                  <Trash size={18} weight="bold" />
                  <span>Elimina</span>
                </button>
                <button
                  onClick={() => runBulkAction("archive")}
                  disabled={bulkBusy}
                  className="flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 active:bg-white/30 px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="email-bulk-archive"
                  title="Sposta in archivio"
                >
                  <Archive size={18} weight="bold" />
                  <span>Archivia</span>
                </button>
                <button
                  onClick={() => runBulkAction("mark-read")}
                  disabled={bulkBusy}
                  className="flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 active:bg-white/30 px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="email-bulk-mark-read"
                  title="Segna come letto"
                >
                  <Envelope size={18} weight="bold" />
                  <span>Segna come letto</span>
                </button>
                <button
                  onClick={() => runBulkAction("mark-unread")}
                  disabled={bulkBusy}
                  className="hidden sm:flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 active:bg-white/30 px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="email-bulk-mark-unread"
                  title="Segna come non letto"
                >
                  <Envelope size={18} />
                  <span>Non letto</span>
                </button>
                <button
                  onClick={clearSelection}
                  className="ml-auto flex items-center gap-1 rounded-md hover:bg-white/10 px-2 py-1.5 text-xs"
                  data-testid="email-bulk-clear"
                  title="Deseleziona tutte"
                >
                  <X size={14} />
                  <span>Annulla</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 border-b px-2 py-1 text-slate-500">
                <button className="p-1 rounded hover:bg-slate-100" title="Archivia" data-testid="email-action-archive"><Archive size={14} /></button>
                <button
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-50"
                  title="Elimina"
                  onClick={() => deleteMessages()}
                  disabled={deleting || !selectedMsg?.uid}
                  data-testid="email-action-delete"
                ><Trash size={14} /></button>
                <button className="p-1 rounded hover:bg-slate-100" title="Segna come letto" data-testid="email-action-read"><Envelope size={14} /></button>
                <button className="p-1 rounded hover:bg-slate-100" title="Sposta" data-testid="email-action-move"><PackageIconStub /></button>
                <button className="p-1 rounded hover:bg-slate-100" title="Etichetta" data-testid="email-action-label"><Tag size={14} /></button>
              </div>
            )}
            {(selectedFolder === "inbox" ? inbox.loading : folderCache[selectedFolder]?.loading) ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400" data-testid="email-list-loading">
                <CircleNotch className="animate-spin mr-2" /> Caricamento…
              </div>
            ) : messagesToShow.length === 0 ? (
              <EmptyList label={activeFolder.label} />
            ) : (
              <div className="flex-1 overflow-auto" data-testid="email-list-items">
                {messagesToShow.map((m) => (
                  <InboxRow
                    key={m.uid || m.message_id}
                    msg={m}
                    active={selectedMsg?.uid === m.uid}
                    selected={selectedUids.has(m.uid)}
                    onToggleSelect={toggleSelectUid}
                    onClick={() => setSelectedMsg(m)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Pannello lettura */}
          <section className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col" data-testid="email-reader">
            {selectedMsg ? (
              <div className="p-4 space-y-3 flex-1 overflow-auto" data-testid="email-reader-content">
                <div>
                  <h2 className="font-display text-lg font-semibold text-slate-900">{selectedMsg.subject}</h2>
                  <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                    <span><b className="text-slate-700">{selectedMsg.from_name || selectedMsg.from_email}</b> &lt;{selectedMsg.from_email}&gt;</span>
                    <span>{selectedMsg.date}</span>
                  </div>
                </div>
                {/* Iter209cr — Corpo email decodificato (RFC 2047 + MIME walk). */}
                {messageBody.loading ? (
                  <div className="pt-2 border-t text-sm text-slate-500 flex items-center gap-2" data-testid="email-reader-body-loading">
                    <CircleNotch className="animate-spin" size={14} /> Caricamento contenuto…
                  </div>
                ) : messageBody.error ? (
                  <div className="pt-2 border-t text-sm text-red-600" data-testid="email-reader-body-error">
                    {messageBody.error}
                  </div>
                ) : messageBody.html ? (
                  <div
                    className="pt-2 border-t prose prose-sm max-w-none text-sm text-slate-800"
                    data-testid="email-reader-body-html"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: messageBody.html }}
                  />
                ) : messageBody.text ? (
                  <pre
                    className="pt-2 border-t text-sm text-slate-800 whitespace-pre-wrap font-sans"
                    data-testid="email-reader-body-text"
                  >{messageBody.text}</pre>
                ) : (
                  <div className="pt-2 border-t text-sm text-slate-400 italic" data-testid="email-reader-body-empty">
                    (Nessun contenuto)
                  </div>
                )}
                {messageBody.attachments.length > 0 && (
                  <div className="pt-2 border-t" data-testid="email-reader-attachments">
                    <div className="text-[11px] uppercase font-bold text-slate-500 tracking-widest mb-1">
                      Allegati
                    </div>
                    <ul className="space-y-1">
                      {messageBody.attachments.map((a, i) => {
                        const idx = a.index ?? i;
                        const busy = downloadingAtt === idx;
                        return (
                          <li key={idx}>
                            <button
                              type="button"
                              onClick={() => downloadAttachment(selectedMsg.uid, idx, a.filename)}
                              disabled={busy}
                              className="w-full flex items-center gap-2 text-xs text-slate-700 rounded px-1.5 py-1 hover:bg-slate-100 hover:text-indigo-700 text-left disabled:opacity-60"
                              title="Scarica allegato"
                              data-testid={`email-attachment-download-${idx}`}
                            >
                              {busy ? <CircleNotch size={12} className="animate-spin" /> : <Paperclip size={12} />}
                              <span className="truncate underline-offset-2 hover:underline">{a.filename}</span>
                              <span className="text-slate-400 shrink-0">({Math.max(1, Math.round((a.size || 0) / 1024))} KB)</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <ReadingEmpty />
            )}
            <div className="border-t px-3 py-2 flex items-center gap-1 text-slate-400 text-xs">
              <button className="px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-1 text-slate-600 disabled:opacity-50" onClick={() => openReplyCompose("reply")} disabled={!selectedMsg || messageBody.loading} data-testid="email-reader-reply"><ArrowBendUpLeft size={12} /> Rispondi</button>
              <button className="px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-1 text-slate-600 disabled:opacity-50" onClick={() => openReplyCompose("reply_all")} disabled={!selectedMsg || messageBody.loading} data-testid="email-reader-reply-all"><ArrowBendDoubleUpRight size={12} /> Rispondi a tutti</button>
              <button className="px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-1 text-slate-600 disabled:opacity-50" onClick={() => openReplyCompose("forward")} disabled={!selectedMsg || messageBody.loading} data-testid="email-reader-forward"><ArrowBendUpRight size={12} /> Inoltra</button>
              <button className="px-2 py-1 rounded hover:bg-slate-100 ml-auto" disabled data-testid="email-reader-archive"><Archive size={12} /></button>
              <button
                className="px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-500"
                onClick={() => deleteMessages()}
                disabled={deleting || !selectedMsg?.uid}
                data-testid="email-reader-delete"
              ><Trash size={12} /></button>
              <button className="px-2 py-1 rounded hover:bg-slate-100" disabled data-testid="email-reader-print"><Printer size={12} /></button>
            </div>
          </section>
        </div>
      )}

      <ComposeWindow
        key={composeKey}
        open={composeOpen}
        onClose={() => {
          setComposeOpen(false);
          if (composeInitial) { setComposeInitial(null); setComposeKey((k) => k + 1); }
        }}
        signatureHtml={user?.email_signature_html || ""}
        initial={composeInitial}
      />
    </div>
  );
}

/** Icona "sposta" (usiamo Archive stilizzata al posto di un'icona ad-hoc). */
function PackageIconStub() {
  return <ArrowUp size={14} weight="regular" />;
}

// Iter209dzv — Helper per Rispondi / Rispondi a tutti / Inoltra.
const _escHtml = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const _emailsOf = (s) => (String(s || "").match(/[^\s<>,;"']+@[^\s<>,;"']+/g) || []).map((e) => e.toLowerCase());
const _prefixSubject = (prefix, subject) => {
  const s = (subject || "").trim();
  const re = prefix === "Re:" ? /^re:\s*/i : /^(fwd?|i):\s*/i;
  return re.test(s) ? s : `${prefix} ${s}`.trim();
};

export function buildReplyDraft(mode, msg, body, myEmail, signatureHtml = "") {
  const me = (myEmail || "").toLowerCase();
  const fromAddr = _emailsOf(body.reply_to)[0] || (msg.from_email || _emailsOf(body.from)[0] || "").toLowerCase();
  const quotedContent = body.html || `<pre style="white-space:pre-wrap;font-family:inherit">${_escHtml(body.text)}</pre>`;
  const sender = _escHtml(msg.from_name ? `${msg.from_name} <${msg.from_email}>` : msg.from_email);
  const sig = signatureHtml ? `<p><br/></p>${signatureHtml}` : "";
  if (mode === "forward") {
    const header = `<p>---------- Messaggio inoltrato ----------<br/>Da: ${sender}<br/>Data: ${_escHtml(msg.date)}<br/>Oggetto: ${_escHtml(msg.subject)}<br/>A: ${_escHtml(body.to)}${body.cc ? `<br/>Cc: ${_escHtml(body.cc)}` : ""}</p>`;
    return {
      title: "Inoltra", to: "", cc: "",
      subject: _prefixSubject("Fwd:", msg.subject),
      body: `<p><br/></p>${sig}<p><br/></p>${header}${quotedContent}`,
      in_reply_to: "", references: "",
    };
  }
  let cc = "";
  if (mode === "reply_all") {
    const seen = new Set([me, fromAddr]);
    cc = [..._emailsOf(body.to), ..._emailsOf(body.cc)]
      .filter((e) => (seen.has(e) ? false : (seen.add(e), true)))
      .join(", ");
  }
  return {
    title: mode === "reply_all" ? "Rispondi a tutti" : "Rispondi",
    to: fromAddr, cc,
    subject: _prefixSubject("Re:", msg.subject),
    body: `<p><br/></p>${sig}<p><br/></p><p>Il ${_escHtml(msg.date)}, ${sender} ha scritto:</p><blockquote style="margin:0 0 0 .8ex;border-left:2px solid #ccc;padding-left:1ex">${quotedContent}</blockquote>`,
    in_reply_to: body.message_id || msg.message_id || "",
    references: body.references || "",
  };
}
